import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getParagraph14BoldTypography } from "../../../Typography";
import InstantBidPanel from "./InstantBidPanel";
import Button from "../../Buttons/Button";
import AuctionTimer from "../AuctionTimer";
import { useTimer } from "../../../hooks/useTimer";
import { IListing } from "../../../commonTypes";
import { getVerticalGap } from "../../../styles";
import ShareBidButton from "../../ShareBidButton/ShareBidButton";
import { shouldShowTimer } from "../../../../helpers/listingHelper";
import Typography from "../../Typography/Typography";
import { getFormattedAmountWithLabel } from "../../../../helpers/format";

const Root = styled.div``;

const Container = styled.div`
  display: flex;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.backgroundColor};
  backdrop-filter: ${({ theme }) => theme.filterBlur};
  padding: 20px;
  flex-direction: column;

  hr {
    border-color: ${({ theme }) => theme.borderColor};
    border-style: solid;
    border-bottom-width: 0;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.darkBorderColor};
  margin-top: 20px;
`;

const InstantBidDetailsContainer = styled.div``;

const PurchaseButtonLabel = styled.p`
  ${getParagraph14BoldTypography};
  margin: auto;
  color: ${({ theme }) => theme.primaryColor};
`;

const Timer = styled.div`
  margin-top: 20px;
`;

const Buttons = styled.div`
  padding-top: 32px;
  ${getVerticalGap("10px")};
`;

type Props = {
  listing: IListing;
  price: string;
  priceUsd: string;
  endTime: string;
  onClick: () => void;
};

const InstantBidDetails = ({
  listing,
  price,
  priceUsd,
  onClick,
  endTime,
}: Props) => {
  const { t } = useTranslation();

  const { isComplete, label, onTimerCompleted } = useTimer({
    inProgressLabel: t("sale-ends"),
    endedLabel: t("sale-ended"),
  });

  return (
    <Root>
      <Container>
        <InstantBidDetailsContainer>
          <Typography secondary>{listing.deployment.chain.name}</Typography>
          <InstantBidPanel
            formattedPrice={getFormattedAmountWithLabel(
              listing.amount,
              listing.asset
            )}
            priceUsd={priceUsd}
          />
        </InstantBidDetailsContainer>
        {shouldShowTimer(endTime) && (
          <>
            <Divider />
            <Timer>
              <AuctionTimer
                title={t(label)}
                opacity={"0.7"}
                endTime={endTime}
                onComplete={onTimerCompleted}
                showWhenEnded={false}
              />
            </Timer>
          </>
        )}
        <Buttons>
          <ShareBidButton music={listing.music} listingId={listing.id} />
          <Button
            label={
              <PurchaseButtonLabel>
                {`${t("instant-buy-button-label")}
                ${price} ${listing.asset.symbol}`}
              </PurchaseButtonLabel>
            }
            fullWidth
            height="48px"
            onClick={onClick}
            disabled={isComplete}
          />
        </Buttons>
      </Container>
    </Root>
  );
};

export default InstantBidDetails;
